import { useMutation, useSuspenseQuery } from "@apollo/client";
import { Button, DataTable, PrimaryFullScreenSpinner, toast } from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import {
  createdAtColumn,
  jobIdColumn,
  lockedColumn,
  PreformattedText,
  statusColumn,
  taskIdentifierColumn,
} from "~/components/platform-jobs/columns";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/jobs")({
  head: () => opsHead("Jobs"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  const jobsQuery = useSuspenseQuery(
    graphql(`
      query AdminPlatformJobs {
        platformJobs {
          id
          key
          taskIdentifier
          queueName
          createdAt
          attempts
          maxAttempts
          lastError
          lockedAt
          lockedBy
        }
      }
    `),
  );

  const [resetPlatformJobAttempts, { loading: resetLoading }] = useMutation(
    graphql(`
      mutation AdminResetPlatformJobAttempts(
        $input: ResetPlatformJobAttemptsInput!
      ) {
        resetPlatformJobAttempts(input: $input) {
          success
        }
      }
    `),
  );
  const [permafailPlatformJob, { loading: permafailLoading }] = useMutation(
    graphql(`
      mutation AdminPermafailPlatformJob($input: PermafailPlatformJobInput!) {
        permafailPlatformJob(input: $input) {
          success
        }
      }
    `),
  );
  const [cleanupPlatformJobQueue, { loading: cleanupLoading }] = useMutation(
    graphql(`
      mutation AdminCleanupPlatformJobQueue {
        cleanupPlatformJobQueue {
          success
        }
      }
    `),
  );

  const jobListItems = jobsQuery.data.platformJobs ?? [];
  const mutationLoading = resetLoading || permafailLoading;

  const runJobAction = async ({
    jobId,
    actionKind,
  }: {
    jobId: string;
    actionKind: "job.reset_attempts" | "job.permafail";
  }) => {
    try {
      if (actionKind === "job.reset_attempts") {
        await resetPlatformJobAttempts({
          variables: {
            input: {
              jobId,
            },
          },
        });
      } else {
        await permafailPlatformJob({
          variables: {
            input: {
              jobId,
            },
          },
        });
      }

      await jobsQuery.refetch();
      toast({
        kind: "success",
        title: "Job updated",
        autoHide: true,
      });
    } catch (error) {
      toast({
        kind: "error",
        title: "Failed to update job",
        description: String(error),
      });
    }
  };

  const handleCleanup = async () => {
    try {
      await cleanupPlatformJobQueue();
      await jobsQuery.refetch();
      toast({
        kind: "success",
        title: "Job queue cleanup completed",
        autoHide: true,
      });
    } catch (error) {
      toast({
        kind: "error",
        title: "Failed to cleanup job queue",
        description: String(error),
      });
    }
  };

  return (
    <AdminPageContainer testId="admin-platform-jobs">
      <AdminPageHeader
        title="Jobs"
        buttons={[
          <Button
            key="job-queue-cleanup"
            disabled={cleanupLoading}
            onClick={() => {
              void handleCleanup();
            }}
          >
            Cleanup
          </Button>,
        ]}
      />
      <DataTable
        data={jobListItems}
        activeRowId={expandedJobId ?? undefined}
        onRowClick={(row) => {
          const active = row.original.id === expandedJobId;
          setExpandedJobId(active ? null : row.original.id);
        }}
        columnSortProps={{
          id: "jobId",
          desc: true,
        }}
        isRowExpanded={(row) => row.original.id === expandedJobId}
        renderExpandedRow={(row) => {
          const job = row.original;
          return (
            <div className="rounded-md p-4">
              <h3 className="mb-3 font-semibold text-sm">Job #{job.id}</h3>
              <DetailRow
                label="Job Key"
                value={<PreformattedText>{job.key}</PreformattedText>}
              />
              <DetailRow
                label="Queue Name"
                value={<PreformattedText>{job.queueName}</PreformattedText>}
              />
              <DetailRow
                label="Attempts"
                value={`${job.attempts} of ${job.maxAttempts}${
                  job.attempts === job.maxAttempts ? " (max. reached)" : ""
                }`}
              />
              <DetailRow className="border-b-transparent" label="Last Error">
                <pre>{job.lastError}</pre>
              </DetailRow>
              <div className="mt-3 flex flex-row justify-end gap-2">
                <Button
                  variant="default"
                  disabled={job.attempts !== job.maxAttempts || mutationLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    void runJobAction({
                      actionKind: "job.reset_attempts",
                      jobId: job.id,
                    });
                  }}
                >
                  Reset Attempts
                </Button>
                <Button
                  variant="destructive"
                  disabled={job.attempts === job.maxAttempts || mutationLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    void runJobAction({
                      actionKind: "job.permafail",
                      jobId: job.id,
                    });
                  }}
                >
                  Permafail
                </Button>
              </div>
            </div>
          );
        }}
        columns={[
          jobIdColumn,
          taskIdentifierColumn,
          createdAtColumn,
          statusColumn,
          lockedColumn,
        ]}
        className="overflow-hidden border border-zinc-200"
      />
    </AdminPageContainer>
  );
}
